import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { formatPrice, formatDate } from "../utils/format";
import OrderTimeline from "../components/shared/OrderTimeline";
import { Package, ChevronDown, ChevronUp, RotateCcw, Truck, MapPin, Gift } from "lucide-react";
import { getEstimatedDeliveryWindow } from "../utils/shipping";
import SEO from "../components/shared/SEO";

const STATUS_BADGE = {
  confirmed:  "badge-info",
  processing: "badge-warning",
  shipped:    "badge-default",
  delivered:  "badge-success",
  cancelled:  "badge-danger",
};

export default function AccountOrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!user) return;
    getDocs(query(collection(db, "orders"), where("userId", "==", user.uid)))
      .then(s => { 
        const ordersList = s.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
        setOrders(ordersList);
        setLoading(false);
      });
  }, [user]);

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh" }}>
      <SEO title="My Orders" />
      <div className="container section-sm" style={{ maxWidth: 840 }}>
        {/* Navigation Tabs between Orders and Referrals */}
        <div style={{ display: "flex", gap: 12, marginBottom: 28, borderBottom: "1px solid var(--border)", paddingBottom: 12 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 14,
              color: "#fff",
              background: "var(--brand)",
            }}
          >
            <Package size={16} /> My Orders
          </div>
          <Link
            to="/account/referrals"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 8,
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 14,
              color: "var(--text-muted)",
              background: "transparent",
            }}
          >
            <Gift size={16} /> Refer & Earn
          </Link>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32, flexWrap: "wrap", gap: 12 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 700 }}>My Orders</h1>
          <Link to="/request-return" className="btn btn-secondary btn-sm">
            <RotateCcw size={14} /> Request Return
          </Link>
        </div>

        {loading ? (
          <div className="loader"><div className="spinner" /></div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <Package size={48} color="var(--text-muted)" style={{ marginBottom: 16 }} />
            <h3 style={{ fontFamily: "var(--font-head)", marginBottom: 8 }}>No orders yet</h3>
            <p style={{ color: "var(--text-muted)", marginBottom: 24 }}>Start shopping to see your orders here.</p>
            <Link to="/shop" className="btn btn-primary">Browse Products</Link>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {orders.map(order => {
              const isOpen = expanded === order.id;
              return (
                <div key={order.id} className="card" style={{ overflow: "hidden" }}>
                  {/* Order header */}
                  <div style={{ padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, cursor: "pointer" }}
                    onClick={() => setExpanded(isOpen ? null : order.id)}>
                    <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                      {/* Thumbnails */}
                      <div style={{ display: "flex", gap: 4 }}>
                        {order.items?.slice(0, 3).map((item, i) => (
                          <div key={i} style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", background: "var(--surface)", border: "1px solid var(--border)", flexShrink: 0 }}>
                            {item.image
                              ? <img src={item.image} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>📦</div>}
                          </div>
                        ))}
                        {order.items?.length > 3 && (
                          <div style={{ width: 44, height: 44, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>
                            +{order.items.length - 3}
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 600, fontSize: 15 }}>Order #{order.id.slice(-8).toUpperCase()}</span>
                          <span
                            className="badge"
                            style={{
                              fontSize: 11,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              background: order.fulfillmentType === "pickup" ? "rgba(16, 185, 129, 0.12)" : "rgba(59, 130, 246, 0.12)",
                              color: order.fulfillmentType === "pickup" ? "#047857" : "#1D4ED8",
                              fontWeight: 700,
                              padding: "2px 8px",
                              borderRadius: 6,
                            }}
                          >
                            {order.fulfillmentType === "pickup" ? (
                              <><MapPin size={11} /> Station Pickup</>
                            ) : (
                              <><Truck size={11} /> Door Delivery</>
                            )}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>
                          {formatDate(order.createdAt)} · {order.items?.length} item{order.items?.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16, color: "var(--brand)" }}>{formatPrice(order.total)}</div>
                        <span className={`badge ${STATUS_BADGE[order.status] || "badge-default"}`} style={{ marginTop: 4, textTransform: "capitalize" }}>{order.status}</span>
                      </div>
                      {isOpen ? <ChevronUp size={18} color="var(--text-muted)" /> : <ChevronDown size={18} color="var(--text-muted)" />}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isOpen && (
                    <div style={{ borderTop: "1px solid var(--border)", padding: "24px 20px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 32 }}>
                        {/* Items */}
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: 14 }}>Items</p>
                          {order.items?.map((item, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                              <div>
                                <div style={{ fontWeight: 500 }}>{item.name}</div>
                                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Qty: {item.qty}</div>
                              </div>
                              <div style={{ fontWeight: 600 }}>{formatPrice(item.price * item.qty)}</div>
                            </div>
                          ))}
                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontWeight: 700 }}>
                            <span>Total</span>
                            <span style={{ color: "var(--brand)" }}>{formatPrice(order.total)}</span>
                          </div>
                          {order.status === "delivered" && (
                            <Link to="/returns" className="btn btn-secondary btn-sm" style={{ marginTop: 14 }}>
                              <RotateCcw size={13} /> Request Return
                            </Link>
                          )}
                        </div>

                        {/* Timeline + Delivery */}
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: 14 }}>Order Tracking</p>
                          <OrderTimeline
                            status={order.status}
                            createdAt={order.createdAt}
                            fulfillmentType={order.fulfillmentType || "delivery"}
                            estimatedDelivery={order.estimatedDeliveryDate || getEstimatedDeliveryWindow(order.createdAt, 7).dateText}
                          />

                          <div style={{ marginTop: 20, padding: "14px 16px", background: "var(--surface)", borderRadius: 10 }}>
                            <p style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: 6 }}>
                              {order.fulfillmentType === "pickup" ? (
                                <><MapPin size={13} color="var(--brand)" /> Station Pickup Location</>
                              ) : (
                                <><Truck size={13} color="var(--brand)" /> Door Delivery Address</>
                              )}
                            </p>
                            {order.fulfillmentType === "pickup" ? (
                              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                                <p style={{ margin: "0 0 2px", fontWeight: 700, color: "var(--brand)" }}>
                                  🏪 {order.delivery?.pickupLocationName || order.pickupLocation?.name || "Afriport Station Hub"}
                                </p>
                                <p style={{ margin: "0 0 4px", color: "var(--text)" }}>
                                  {order.delivery?.pickupAddress || order.pickupLocation?.address},{" "}
                                  {order.delivery?.pickupCity || order.pickupLocation?.city}, {order.delivery?.state} State
                                </p>
                                {(order.delivery?.pickupPhone || order.pickupLocation?.phone) && (
                                  <p style={{ margin: "2px 0 0", color: "var(--text-muted)" }}>
                                    📞 Hub Tel: {order.delivery?.pickupPhone || order.pickupLocation?.phone}
                                  </p>
                                )}
                                {(order.delivery?.pickupHours || order.pickupLocation?.operatingHours) && (
                                  <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: 12 }}>
                                    🕒 Hours: {order.delivery?.pickupHours || order.pickupLocation?.operatingHours}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
                                <span style={{ fontWeight: 600 }}>{order.delivery?.fullName}</span> · {order.delivery?.phone}<br />
                                {order.delivery?.address}<br />
                                {order.delivery?.city}, {order.delivery?.state} State
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
